import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Icon, Button, Tooltip } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import MentionBadge from './MentionBadge';
import CodeBlock from './CodeBlock';
import './MessageContent.css';

interface Attachment {
  id: string;
  fileName: string;
  fileSize?: number;
  fileType?: string;
  mimeType?: string;
  url?: string;
  isImage?: boolean;
}

interface MessageContentProps {
  content: string;
  htmlContent?: string | null;
  mentions?: Array<{
    type: 'mindmap' | 'node' | 'user' | 'organization';
    id: string;
    label: string;
    metadata?: any;
  }> | null;
  attachments?: Attachment[] | null;
  onMentionClick?: (mention: any) => void;
}

/**
 * Renders message content with mentions and attachments
 */
const MessageContent: React.FC<MessageContentProps> = ({
  content,
  htmlContent,
  mentions,
  attachments,
  onMentionClick,
}) => {
  // Debug log attachments
  React.useEffect(() => {
    if (attachments && attachments.length > 0) {
      console.log('📎 MessageContent rendering with attachments:', attachments);
    }
  }, [attachments]);

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (mimeType?: string) => {
    if (!mimeType) return IconNames.DOCUMENT;
    if (mimeType.startsWith('image/')) return IconNames.MEDIA;
    if (mimeType === 'application/pdf') return IconNames.DOCUMENT;
    if (mimeType.includes('word') || mimeType.includes('document')) return IconNames.DOCUMENT_OPEN;
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return IconNames.TH;
    if (mimeType.includes('zip') || mimeType.includes('archive')) return IconNames.COMPRESSED;
    if (mimeType.includes('text')) return IconNames.CODE;
    return IconNames.PAPERCLIP;
  };

  const getFileExtension = (fileName: string) => {
    const parts = fileName.split('.');
    return parts.length > 1 ? `.${parts[parts.length - 1]}` : '';
  };

  const handleDownload = (attachment: Attachment) => {
    if (attachment.url) {
      window.open(attachment.url, '_blank');
    }
  };

  return (
    <div className="message-display">
      {/* Message Text */}
      <div className="message-content-text">
        <ReactMarkdown 
          remarkPlugins={[remarkGfm]}
          components={{
            code({ node, inline, className, children, ...props }) {
              const match = /language-(\w+)/.exec(className || '');
              const language = match ? match[1] : '';
              
              // Inline code - render normally
              if (inline) {
                return (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              }
              
              // Block code - use CodeBlock with syntax highlighting
              return (
                <CodeBlock
                  language={language || 'text'}
                  isDark={true}
                >
                  {String(children).replace(/\n$/, '')}
                </CodeBlock>
              );
            }
          }}
        >
          {content}
        </ReactMarkdown>
      </div>

      {/* Attachments */}
      {attachments && attachments.length > 0 && (
        <div className="message-attachments">
          {attachments.map((attachment) => (
            <div key={attachment.id} className="attachment-item">
              <div className="attachment-icon">
                <Icon 
                  icon={getFileIcon(attachment.mimeType || attachment.fileType)} 
                  size={20}
                  color={attachment.isImage ? '#10b981' : '#6b7280'}
                />
              </div>
              
              <div className="attachment-info">
                <div className="attachment-name">
                  <Tooltip content={attachment.fileName}>
                    <span>{attachment.fileName}</span>
                  </Tooltip>
                </div>
                <div className="attachment-meta">
                  <span className="file-ext">{getFileExtension(attachment.fileName)}</span>
                  {attachment.fileSize && (
                    <>
                      <span className="separator">•</span>
                      <span className="file-size">{formatFileSize(attachment.fileSize)}</span>
                    </>
                  )}
                  {(attachment.mimeType || attachment.fileType) && (
                    <>
                      <span className="separator">•</span>
                      <span className="mime-type">
                        {(attachment.mimeType || attachment.fileType || '').split('/')[1] || 'file'}
                      </span>
                    </>
                  )}
                </div>
              </div>

              <Button
                icon={IconNames.DOWNLOAD}
                minimal
                small
                onClick={() => handleDownload(attachment)}
                title="Download file"
              />
            </div>
          ))}
        </div>
      )}

      {/* Mentions */}
      {mentions && mentions.length > 0 && (
        <div className="message-mentions">
          <span className="mentions-label">Mentioned:</span>
          {mentions.map((mention, idx) => (
            <MentionBadge
              key={`${mention.id}-${idx}`}
              type={mention.type}
              label={mention.label}
              id={mention.id}
              onClick={() => onMentionClick?.(mention)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MessageContent;
